import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Alert as MuiAlert, Button, DialogActions, Box, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import * as Yup from "yup";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useDialog } from "../../../../contexts/DialogContext";
import usePlan from "../../../../hooks/usePlan";
import SearchableAutoComplete from "../../../../components/SearchableAutoComplete";
import useTenant from "../../../../hooks/useTenant";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";

const initialValues = {
  tenant: [],
};

const validationSchema = Yup.object().shape({
  tenant: Yup.array()
    .of(
      Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.number().required(),
      }),
    )
    .min(1, "You need at least one tenant")
    .required("Please select the tenant"),
});

const Alert = styled(MuiAlert)(spacing);

const MapTenantModal = ({ planId }) => {
  const { unSetDialog } = useDialog();

  const dispatch = useDispatch();

  const methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const {
    autoSearchTenant,
    tenantAutoSearchResults,
    autoSearchLoading,
    isSubmitting,
    getTenantPlans,
    addTenantPlan,
  } = useTenant();
  const currentPayload = {
    filters: [],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [],
  };

  const [payload, setPayload] = useState(currentPayload);

  const [tenantPlanPayload, setTenantPlanPayload] = useState({
    filters: [],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [],
  });

  const [tenantOptions, setTenantOptions] = useState([]);

  const fetchData = async () => {
    await autoSearchTenant(payload);
  };

  useEffect(() => {
    if (tenantAutoSearchResults.data.length !== 0) {
      setTenantOptions([...tenantOptions, ...tenantAutoSearchResults.data]);
    }
  }, [tenantAutoSearchResults]);

  useEffect(() => {
    fetchData();
  }, [payload]);

  const { handleSubmit, control, reset } = methods;

  const onSubmit = async (data) => {
    try {
      const planDetails = [];
      const tenants = data.tenant;

      for (let i = 0; i < tenants.length; i++) {
        const planTenantPayload = {
          tenantId: tenants[i]["value"],
          planId: planId,
        };
        planDetails.push(planTenantPayload);
      }
      //console.log(planDetails, "planDetails");
      await addTenantPlan(planDetails);
      await getTenantPlans(tenantPlanPayload);
      reset();
      dispatch(showSuccessSnackbar("Tenant mapped successfully!"));
      unSetDialog("map_tenant_modal");
    } catch (err) {
      dispatch(showErrorSnackbar(err.message));
    }
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", marginTop: 10 }}>
            <SearchableAutoComplete
              control={control}
              data={tenantOptions}
              label="Choose a Tenant"
              payload={payload}
              setPayload={setPayload}
              loading={autoSearchLoading}
              name="tenant"
              multiple={true}
            />
          </Box>
          <DialogActions>
            <Button
              disabled={isSubmitting}
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              endIcon={isSubmitting ? <CircularProgress size={24} /> : null}
            >
              Add
            </Button>

            <Button variant="text" onClick={() => unSetDialog("map_tenant_modal")} color="primary">
              Close
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </React.Fragment>
  );
};
export default MapTenantModal;
